import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { Chip } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

import { getAllocationTypeLabelFromValue } from "utils/constants/helpers";
import { formatDate } from "utils/time";

const RESOURCE_ICONS = {
  EV: "electric_car",
  STORAGE: "battery_charging_full",
  SCPU: "wb_sunny",
  EWH: "water_heater",
  IU: "home",
  IC: "bolt",
  HP: "heat_pump",
};

export default function CommunityCard({
  community,
  canDelete,
  deleting,
  onSelect,
  onConfigure,
  onDelete,
}) {
  const { t } = useTranslation(["community", "common"]);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const created = community.created_at ? formatDate(new Date(community.created_at)) : "—";

  return (
    <div
      className={`rec-comm-card transition-opacity duration-300${
        deleting ? " animate-pulse opacity-40 pointer-events-none" : ""
      }`}
      onClick={() => onSelect(community)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect(community);
      }}
    >
      <div className="rec-comm-card-top">
        <div className="rec-comm-avatar">
          <Icon name="solar_power" fill />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <h3 className="flex-1">{community.name}</h3>
            {community.type === "DEMO" && (
              <Chip className="shrink-0" tone="info">
                {t("list.card.demo_badge").toUpperCase()}
              </Chip>
            )}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="rec-icon-btn !w-[30px] !h-[30px]"
                title={t("list.menu.configure")}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenu((m) => !m);
                }}
              >
                <Icon name="more_vert" style={{ fontSize: 19 }} />
              </button>
              {menu && (
                <div className="rec-pop !min-w-[170px]">
                  <button
                    type="button"
                    className="rec-pop-opt"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu(false);
                      onConfigure(community);
                    }}
                  >
                    <Icon name="settings" style={{ fontSize: 17 }} />
                    {t("list.menu.configure")}
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      className="rec-pop-opt"
                      style={{ color: "var(--danger)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenu(false);
                        onDelete(community);
                      }}
                    >
                      <Icon name="delete" style={{ fontSize: 17 }} />
                      {t("delete.remove")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {community.identifier && <div className="code">{community.identifier}</div>}
        </div>
      </div>

      <div className="rec-comm-card-mid">
        <Icon name="location_on" />
        <span className="flex-1">{community.address || t("list.card.address_undefined")}</span>
        {community.resource_types?.length > 0 && (
          <div className="rec-comm-restypes">
            {community.resource_types.map((type) => (
              <Icon
                key={type}
                name={RESOURCE_ICONS[type] || "category"}
                title={t(`list.card.resource_types.${type}`, { defaultValue: type })}
              />
            ))}
          </div>
        )}
      </div>

      <div className="rec-comm-card-foot">
        <div className="rec-comm-stat">
          <b>{community.total_members ?? 0}</b>
          <span>{t("list.card.members")}</span>
        </div>
        <div className="rec-comm-stat info">
          <b>{getAllocationTypeLabelFromValue(community.method_alloc_coeff) || "—"}</b>
          <span>{t("list.card.coefficients")}</span>
        </div>
        <div className="rec-comm-stat">
          <b>{created}</b>
          <span>{t("list.card.created_at")}</span>
        </div>
      </div>
    </div>
  );
}

CommunityCard.propTypes = {
  community: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    identifier: PropTypes.string,
    address: PropTypes.string,
    total_members: PropTypes.number,
    created_at: PropTypes.string,
    type: PropTypes.string,
    method_alloc_coeff: PropTypes.string,
    resource_types: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canDelete: PropTypes.bool,
  deleting: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onConfigure: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

CommunityCard.defaultProps = {
  canDelete: false,
  deleting: false,
};
